import { useT } from "@transifex/react";
import { useId, useState, type FormEvent } from "react";
import { LoginErrorVariants } from "../../context/classMemberLoginErrors";
import InlineFormError from "../../basic/InlineFormError";
import { CODE_INPUT_CLASS, LOGIN_COLUMN_CLASS, PRIMARY_PILL_BUTTON_CLASS } from "./classMemberFormStyles";

interface ClassLoginFormProps {
    showClassCode: boolean;
    initialClassCode?: string;
    initialPersonalCode?: string;
    /** Adds a label and swaps the placeholder for an example code. */
    showPersonalCodeLabel?: boolean;
    error: LoginErrorVariants;
    isSubmitting: boolean;
    onSubmit: (classCode: string, personalCode: string) => void;
}

// Codes are shown in pairs (e.g. "AB CD EF GH"), so pupils may write them down and type them with spaces.
const normalizeCode = (code: string) => code.replace(/\s/g, "").toUpperCase();

const ClassLoginForm = (props: ClassLoginFormProps) => {
    const t = useT();
    const personalCodeInputId = useId();

    const [classCode, setClassCode] = useState(props.initialClassCode ?? "");
    const [personalCode, setPersonalCode] = useState(props.initialPersonalCode ?? "");

    const t_classCodePlaceholder = t("Your class code", { _tags: "ClassMemberLogin, ClassLoginForm" });
    const t_personalCodePlaceholder = t("Your code", { _tags: "ClassMemberLogin, ClassLoginForm" });
    const t_personalCodeLabel = t("Your personal code", { _tags: "ClassMemberLogin, ClassLoginForm" });
    const t_personalCodeExample = t("e.g. ABCDEFGH", { _tags: "ClassMemberLogin, ClassLoginForm" });
    const t_login = t("Login", { _tags: "ClassMemberLogin, ClassLoginForm" });
    const t_errorBoth = t("Please enter valid codes. Did you mistype?", {
        _tags: "ClassMemberLogin, ClassLoginForm",
    });
    const t_errorSingle = t("Please enter a valid code. Did you mistype?", {
        _tags: "ClassMemberLogin, ClassLoginForm",
    });
    const t_errorTransient = t("Login not possible right now. Please try again.", {
        _tags: "ClassMemberLogin, ClassLoginForm",
    });

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (props.isSubmitting) return;
        props.onSubmit(normalizeCode(classCode), normalizeCode(personalCode));
    };

    const getErrorText = () => {
        if (props.error === LoginErrorVariants.NONE) return null;
        if (props.error === LoginErrorVariants.TRANSIENT) return t_errorTransient;
        return props.showClassCode ? t_errorBoth : t_errorSingle;
    };

    const errorText = getErrorText();

    const isLoginDisabled = props.isSubmitting || !personalCode.trim() || (props.showClassCode && !classCode.trim());

    return (
        <form
            onSubmit={handleSubmit}
            className={`${LOGIN_COLUMN_CLASS} gap-s`}
        >
            {props.showClassCode && (
                <input
                    type="text"
                    value={classCode}
                    onChange={(e) => setClassCode(e.target.value)}
                    data-testid="class-login-class-code-input"
                    placeholder={t_classCodePlaceholder}
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    className={CODE_INPUT_CLASS}
                />
            )}
            {props.showPersonalCodeLabel && (
                <label
                    htmlFor={personalCodeInputId}
                    className="self-start text-m text-p6-grey-dark"
                >
                    {t_personalCodeLabel}
                </label>
            )}
            <input
                id={personalCodeInputId}
                type="text"
                value={personalCode}
                onChange={(e) => setPersonalCode(e.target.value)}
                data-testid="class-login-personal-code-input"
                placeholder={props.showPersonalCodeLabel ? t_personalCodeExample : t_personalCodePlaceholder}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                className={CODE_INPUT_CLASS}
            />
            {errorText && <InlineFormError isLarge>{errorText}</InlineFormError>}
            <button
                type="submit"
                disabled={isLoginDisabled}
                data-testid="class-login-submit"
                className={PRIMARY_PILL_BUTTON_CLASS}
            >
                {t_login}
            </button>
        </form>
    );
};

export default ClassLoginForm;
